import React, { useMemo } from "react";

export interface PaginationProps {
  /** Current page. 1-based when `oneBased` is true (default), 0-based otherwise. */
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  /** When true (default), pages are numbered 1…n; when false, 0…n-1. */
  oneBased?: boolean;
  disabled?: boolean;
  className?: string;
  showFirstLast?: boolean;
}

const CIRCLE_SIZE = 32;

const circleBtnClass = (variant: "nav" | "page", active?: boolean) =>
  [
    "btn btn-sm rounded-circle d-flex align-items-center justify-content-center",
    variant === "nav"
      ? "btn-outline-secondary"
      : active
        ? "btn-primary"
        : "btn-outline-secondary",
  ].join(" ");

const circleStyle: React.CSSProperties = {
  width: CIRCLE_SIZE,
  height: CIRCLE_SIZE,
  padding: 0,
};

function buildPageItems(
  totalPages: number,
  currentIndex: number
): (number | "…")[] {
  if (totalPages <= 0) return [];

  return Array.from({ length: totalPages }, (_, i) => i)
    .filter(
      (i) =>
        i === 0 || i === totalPages - 1 || Math.abs(i - currentIndex) <= 1
    )
    .reduce<(number | "…")[]>((acc, i, idx, arr) => {
      if (idx > 0 && i - (arr[idx - 1] as number) > 1) acc.push("…");
      acc.push(i);
      return acc;
    }, []);
}

export const Pagination = ({
  currentPage,
  totalPages,
  onPageChange,
  oneBased = true,
  disabled = false,
  className = "",
  showFirstLast = false,
}: PaginationProps) => {
  const currentIndex = oneBased ? currentPage - 1 : currentPage;
  const lastIndex = totalPages - 1;

  const pageItems = useMemo(
    () => buildPageItems(totalPages, currentIndex),
    [totalPages, currentIndex]
  );

  if (totalPages <= 1) return null;

  const toPage = (index: number) => (oneBased ? index + 1 : index);
  const isDisabled = disabled;

  const goTo = (index: number) => {
    if (index < 0 || index > lastIndex || isDisabled) return;
    onPageChange(toPage(index));
  };

  return (
    <div
      className={`d-flex align-items-center justify-content-center ${className}`.trim()}
      style={{ gap: 4 }}
      role="navigation"
      aria-label="Pagination"
    >
      {showFirstLast && (
        <button
          type="button"
          className={circleBtnClass("nav")}
          style={circleStyle}
          disabled={currentIndex === 0 || isDisabled}
          onClick={() => goTo(0)}
          aria-label="First page"
          title="First page"
        >
          <i className="fas fa-angle-double-left" style={{ fontSize: "0.65rem" }} />
        </button>
      )}

      <button
        type="button"
        className={circleBtnClass("nav")}
        style={circleStyle}
        disabled={currentIndex === 0 || isDisabled}
        onClick={() => goTo(currentIndex - 1)}
        aria-label="Previous page"
        title="Previous page"
      >
        <i className="fas fa-chevron-left" style={{ fontSize: "0.65rem" }} />
      </button>

      {pageItems.map((item, idx) =>
        item === "…" ? (
          <span
            key={`ellipsis-${idx}`}
            className="px-1 text-muted"
            style={{ fontSize: "0.75rem" }}
            aria-hidden
          >
            …
          </span>
        ) : (
          <button
            key={item}
            type="button"
            className={circleBtnClass("page", item === currentIndex)}
            style={{
              ...circleStyle,
              fontSize: "0.75rem",
              fontWeight: item === currentIndex ? 600 : 400,
            }}
            onClick={() => goTo(item)}
            disabled={isDisabled}
            aria-label={`Page ${item + 1}`}
            aria-current={item === currentIndex ? "page" : undefined}
          >
            {item + 1}
          </button>
        )
      )}

      <button
        type="button"
        className={circleBtnClass("nav")}
        style={circleStyle}
        disabled={currentIndex >= lastIndex || isDisabled}
        onClick={() => goTo(currentIndex + 1)}
        aria-label="Next page"
        title="Next page"
      >
        <i className="fas fa-chevron-right" style={{ fontSize: "0.65rem" }} />
      </button>

      {showFirstLast && (
        <button
          type="button"
          className={circleBtnClass("nav")}
          style={circleStyle}
          disabled={currentIndex >= lastIndex || isDisabled}
          onClick={() => goTo(lastIndex)}
          aria-label="Last page"
          title="Last page"
        >
          <i className="fas fa-angle-double-right" style={{ fontSize: "0.65rem" }} />
        </button>
      )}
    </div>
  );
};
